import { useState } from 'react'
import { FileClock, Key, Mail, ShieldCheck, Sparkles, Users } from 'lucide-react'
import { Page } from '@/components/Page'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/auth/useAuth'
import { MembersTab } from './tabs/MembersTab'
import { InvitationsTab } from './tabs/InvitationsTab'
import { RolesTab } from './tabs/RolesTab'
import { ApiKeysTab } from './tabs/ApiKeysTab'
import { AuditTab } from './tabs/AuditTab'
import { AiGatewayTab } from './tabs/AiGatewayTab'

type TabId = 'members' | 'invitations' | 'roles' | 'ai-gateway' | 'api-keys' | 'audit'

export function TeamManagementPage() {
  const { hasPermission } = useAuth()
  const [tab, setTab] = useState<TabId>('members')

  const canViewInvites = hasPermission('team.view') || hasPermission('team.invite')
  const canViewRoles = hasPermission('team.view') || hasPermission('team.roles')
  // AI Gateway: visible to anyone with team.view (so members can see their
  // own usage/limit tile) and editable to those with team.ai_gateway.manage.
  const canViewAiGateway = hasPermission('team.view') || hasPermission('team.ai_gateway.manage')
  const canViewApiKeys = hasPermission('api_keys.manage')
  const canViewAudit = hasPermission('audit.view')

  return (
    <Page
      title="Team management"
      description="Manage tenant members, invitations, custom roles, API keys, and the audit log."
      width="wide"
    >
      <Tabs value={tab} onValueChange={(v) => setTab(v as TabId)}>
        <TabsList className="h-auto flex-wrap justify-start gap-1 bg-transparent p-0">
          <TabsTrigger
            value="members"
            className="gap-2 border data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
          >
            <Users className="size-4" /> Members
          </TabsTrigger>
          {canViewInvites ? (
            <TabsTrigger
              value="invitations"
              className="gap-2 border data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
            >
              <Mail className="size-4" /> Invitations
            </TabsTrigger>
          ) : null}
          {canViewRoles ? (
            <TabsTrigger
              value="roles"
              className="gap-2 border data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
            >
              <ShieldCheck className="size-4" /> Custom Roles
            </TabsTrigger>
          ) : null}
          {canViewAiGateway ? (
            <TabsTrigger
              value="ai-gateway"
              className="gap-2 border data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
            >
              <Sparkles className="size-4" /> AI Gateway
            </TabsTrigger>
          ) : null}
          {canViewApiKeys ? (
            <TabsTrigger
              value="api-keys"
              className="gap-2 border data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
            >
              <Key className="size-4" /> API Keys
            </TabsTrigger>
          ) : null}
          {canViewAudit ? (
            <TabsTrigger
              value="audit"
              className="gap-2 border data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
            >
              <FileClock className="size-4" /> Audit Log
            </TabsTrigger>
          ) : null}
        </TabsList>

        <TabsContent value="members" className="mt-4">
          <MembersTab />
        </TabsContent>
        {canViewInvites ? (
          <TabsContent value="invitations" className="mt-4">
            <InvitationsTab />
          </TabsContent>
        ) : null}
        {canViewRoles ? (
          <TabsContent value="roles" className="mt-4">
            <RolesTab />
          </TabsContent>
        ) : null}
        {canViewAiGateway ? (
          <TabsContent value="ai-gateway" className="mt-4">
            <AiGatewayTab />
          </TabsContent>
        ) : null}
        {canViewApiKeys ? (
          <TabsContent value="api-keys" className="mt-4">
            <ApiKeysTab />
          </TabsContent>
        ) : null}
        {canViewAudit ? (
          <TabsContent value="audit" className="mt-4">
            <AuditTab />
          </TabsContent>
        ) : null}
      </Tabs>
    </Page>
  )
}
